import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ClientsRepository } from "@/api/clients/repository";
import { ClientsService } from "@/api/clients/service";
import { Form } from "@/components/controls";
import { AddressControl } from "@/components/controls/AddressControl";
import type { IClientDetailsFormData } from "@/pages/Settings/types";
import type { IClient } from "@/types/users-clients";
import { Button, Drawer, Toast } from "@/ui-kit";
import { AddressUtils } from "@/utils/address";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { addressSchema } from "@/utils/validation";

type IProps = {
  client: IClient;
  onSave: (updatedClient: IClient) => void;
  onDismiss: () => void;
};

const EditModal = ({ client, onSave, onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z.object({
      address: addressSchema,
    });
  }, []);

  const form = useForm<IClientDetailsFormData>({
    defaultValues: {
      address: AddressUtils.getNormalized(client.address),
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const repository = useMemo(() => {
    return new ClientsRepository(new ClientsService(), { abort: true });
  }, []);

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IClientDetailsFormData) => {
      setLoading(true);

      repository
        .updateClient({
          id: client.id,
          userId: client.userId,
          address: formData.address,
        })
        .then((response) => {
          onSave(response);
          Toast.success({
            title: t("toast.addressUpdated"),
          });
          onDismiss();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        );
    })();
  }, [
    handleSubmit,
    repository,
    client.id,
    client.userId,
    onSave,
    onDismiss,
    t,
  ]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer open={true} onOpenChange={onDismiss} closeOnOutsidePress={false}>
        <Drawer.Header title={t("action.editAddress")} />
        <Drawer.Body>
          <Form>
            <AddressControl label={t("label.address")} name="address" />
          </Form>
        </Drawer.Body>
        <Drawer.Footer>
          <Button onClick={handleDismiss} variant="ghost" color="neutral">
            {t("action.cancel")}
          </Button>
          <Button onClick={onSubmit} loading={loading}>
            {t("action.save")}
          </Button>
        </Drawer.Footer>
      </Drawer>
    </FormProvider>
  );
};

export default EditModal;
